import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import Container from '@mui/material/Container'
import CircularProgress from '@mui/material/CircularProgress'

import { useContextHook } from '../store/context'
import ScheduleWizard from '../components/schedule/scheduleWizard'
import { Schedule } from '../types/schedule'
import Alert from '../components/alert/alert'
import { get, post, put, setPageTitle } from '../utils/service'

const ScheduleDetailView = (): JSX.Element => {
  const params = useParams()
  const { state } = useContextHook()
  const navigate = useNavigate()

  const [modelSchedule, setModelSchedule] = React.useState<Schedule | null>(null)

  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [hasLoadError, setHasLoadError] = React.useState<boolean>(false)
  const [isSaving, setIsSaving] = React.useState<boolean>(false)
  const [hasSaveError, setHasSaveError] = React.useState<boolean>(false)
  const [saveSuccessMessage, setSaveSuccessMessage] = React.useState<string>('')

  React.useEffect(() => {
    if (params.scheduleId && state.currentMachine?.url && !modelSchedule?.id) {
      setIsLoading(true)

      get<Schedule>(`schedules/${params.scheduleId}`)
        .then((response) => {
          setIsLoading(false)
          setModelSchedule(response.data)
        })
        .catch(() => {
          setIsLoading(false)
          setHasLoadError(true)
        })
      setPageTitle('Edit schedule')
    } else if (!params.scheduleId && state.currentMachine?.url && !modelSchedule?.id) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      setModelSchedule({
        id: -1,
        url: '',
        batch_no: 0,
        date: today,
        machine: state.currentMachine.url,
        inventory: '',
        inventory_data: null,
        profile: '',
        profile_data: null,
        iterations: [],
      })
      setPageTitle('Create schedule')
    }
  }, [params.scheduleId, state.currentMachine?.url, modelSchedule?.id])

  const saveSchedule = (saveData: Schedule): void => {
    setIsSaving(true)

    const saveUrl = modelSchedule?.url
    const savePromise = saveUrl
      ? put<Schedule, Schedule>(modelSchedule?.url, saveData)
      : post<Schedule, Schedule>('schedules/', saveData)

    savePromise
      .then((savedScheduleData) => {
        setIsSaving(false)
        if (!saveUrl) {
          const machineSlug = params.machineId
          navigate(`/machine/${machineSlug}/schedules/${savedScheduleData.data.id}`, { replace: true })
        }
        setSaveSuccessMessage(saveUrl ? 'Schedule updated' : 'Schedule created')
        setModelSchedule(savedScheduleData.data)
      })
      .catch(() => {
        setIsSaving(false)
        setHasSaveError(true)
      })
  }

  return (
    <Container component="main" maxWidth="md">
      {isLoading && <CircularProgress />}
      {hasSaveError && <Alert severity="error">Could not save schedule</Alert>}
      {saveSuccessMessage && <Alert severity="success">{saveSuccessMessage}</Alert>}
      {hasLoadError && <Alert severity="error">Could not load schedule</Alert>}
      {modelSchedule && state.currentMachine?.id && state.currentCustomer?.id && (
        <ScheduleWizard
          schedule={modelSchedule}
          isSaving={isSaving}
          machineId={state.currentMachine.id}
          customerId={state.currentCustomer.id}
          onSaveSchedule={saveSchedule}
        />
      )}
    </Container>
  )
}

export default ScheduleDetailView
